import { motion, useScroll, useTransform, type MotionValue } from "framer-motion";
import {
  Bot,
  ChartNoAxesColumnIncreasing,
  MousePointer2,
  Phone,
  Puzzle,
  Settings,
  type LucideIcon,
} from "lucide-react";
import { useRef } from "react";
import { Lang, t } from "../i18n";
import logoMarkSrc from "../assets/clawease-logo-mark-vector.svg";

type Capability = {
  key: string;
  icon: LucideIcon;
  x: number;
  rotate: number;
};

const capabilities: Capability[] = [
  { key: "1", icon: Bot, x: -500, rotate: 0 },
  { key: "2", icon: Phone, x: -300, rotate: 0 },
  { key: "3", icon: Puzzle, x: -100, rotate: 0 },
  { key: "4", icon: MousePointer2, x: 100, rotate: 0 },
  { key: "5", icon: ChartNoAxesColumnIncreasing, x: 300, rotate: 0 },
  { key: "6", icon: Settings, x: 500, rotate: 0 },
];

const metrics = ["1", "2", "3", "4"];

function MeetLine({
  x,
  progress,
}: {
  x: number;
  progress: MotionValue<number>;
}) {
  const opacity = useTransform(progress, [0.2, 0.32, 0.78, 0.94], [0, 0.9, 0.9, 0]);
  const endX = 590 + x;
  const controlA = 590 + x * 0.12;
  const controlB = 590 + x * 0.78;
  const path = `M 590 184 C ${controlA} 256, ${controlB} 286, ${endX} 364`;

  return (
    <g>
      <path d={path} fill="none" stroke="#e4e0eb" strokeWidth="1.35" strokeLinecap="round" opacity="0.72" />
      <motion.path
        d={path}
        fill="none"
        stroke="url(#meetLinkGradient)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="132 720"
        style={{ opacity }}
        animate={{ strokeDashoffset: [180, -520] }}
        transition={{
          duration: 3.4,
          repeat: Infinity,
          ease: "linear",
        }}
      />
      <motion.circle cx={endX} cy="364" r="4.2" fill="#9b7de1" style={{ opacity }} />
    </g>
  );
}

function CapabilityCard({
  lang,
  item,
  progress,
  index,
}: {
  lang: Lang;
  item: Capability;
  progress: MotionValue<number>;
  index: number;
}) {
  const x = useTransform(progress, [0, 0.28, 0.78, 1], [item.x * 0.16, item.x, item.x, item.x * 0.28]);
  const y = useTransform(progress, [0, 0.28, 0.78, 1], [-120, 0, 0, -82]);
  const opacity = useTransform(progress, [0.02, 0.2, 0.78, 0.94], [0, 1, 1, 0]);
  const scale = useTransform(progress, [0, 0.28, 0.78, 1], [0.84, 1, 1, 0.9]);
  const Icon = item.icon;

  return (
    <motion.article
      className="absolute left-1/2 top-[368px] -ml-[90px] flex h-[152px] w-[180px] flex-col rounded-[22px] border border-[#e8dfea] bg-white p-4 text-left"
      style={{ x, y, opacity, scale, rotate: item.rotate }}
      transition={{ delay: index * 0.03 }}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#f3eefa] text-[#5223b1]">
          <Icon size={17} strokeWidth={2.3} />
        </div>
        <h3 className="min-w-0 text-[15px] font-extrabold leading-tight text-[#17151c]">
          {t(lang, `meet.cap.${item.key}.title`)}
        </h3>
      </div>
      <p className="mt-4 text-[11.5px] font-semibold leading-[1.45] text-[#746778]">
        {t(lang, `meet.cap.${item.key}.desc`)}
      </p>
    </motion.article>
  );
}

function MobileCapabilityCard({ lang, item }: { lang: Lang; item: Capability }) {
  const Icon = item.icon;

  return (
    <motion.article
      className="rounded-[20px] border border-[#e8dfea] bg-white p-4"
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#f3eefa] text-[#5223b1]">
          <Icon size={17} strokeWidth={2.3} />
        </div>
        <h3 className="text-[15px] font-extrabold text-[#17151c]">{t(lang, `meet.cap.${item.key}.title`)}</h3>
      </div>
      <p className="mt-3 text-[12px] font-semibold leading-relaxed text-[#746778]">
        {t(lang, `meet.cap.${item.key}.desc`)}
      </p>
    </motion.article>
  );
}

function MeetMetrics({
  lang,
  opacity,
  y,
}: {
  lang: Lang;
  opacity?: MotionValue<number>;
  y?: MotionValue<number>;
}) {
  return (
    <motion.div
      className="relative w-screen border-y border-[#e8dfea] bg-[#f8f9fa]"
      style={opacity ? { opacity, y, marginLeft: "calc(50% - 50vw)" } : { marginLeft: "calc(50% - 50vw)" }}
      initial={opacity ? false : { opacity: 0, y: 24 }}
      whileInView={opacity ? undefined : { opacity: 1, y: 0 }}
      viewport={opacity ? undefined : { once: true, amount: 0.3 }}
      transition={{ duration: 0.58, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="mx-auto grid w-full max-w-[1280px] grid-cols-2 gap-x-8 gap-y-8 px-8 py-12 md:grid-cols-4 md:gap-x-14 lg:px-10 lg:py-16">
        {metrics.map((key) => (
          <div key={key} className="min-w-0">
            <div className="text-[42px] font-black leading-none tracking-[-0.04em] md:text-[48px]">
              <span
                className="inline-block text-transparent"
                style={{
                  backgroundImage: "linear-gradient(92deg, #6f2bd3 0%, #7a2fd0 48%, #b72ac6 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {t(lang, `meet.metric.${key}.value`)}
              </span>
            </div>
            <div
              className={
                lang === "en"
                  ? "mt-4 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-[#7a6079]"
                  : "mt-4 text-[12px] font-extrabold tracking-normal text-[#7a6079]"
              }
            >
              {t(lang, `meet.metric.${key}.label`)}
            </div>
            <div
              className={
                lang === "en"
                  ? "mt-2 text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#7a6079]"
                  : "mt-2 text-[11px] font-semibold text-[#7a6079]"
              }
            >
              {t(lang, `meet.metric.${key}.sub`)}
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export function MeetClawEase({ lang }: { lang: Lang }) {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 82%", "end 18%"],
  });

  const headerOpacity = useTransform(scrollYProgress, [0, 0.14, 0.86, 1], [0, 1, 1, 0]);
  const headerY = useTransform(scrollYProgress, [0, 0.14, 0.86, 1], [28, 0, 0, -26]);
  const centerOpacity = useTransform(scrollYProgress, [0.04, 0.18, 0.82, 0.96], [0, 1, 1, 0]);
  const centerY = useTransform(scrollYProgress, [0, 0.24, 0.82, 1], [42, 0, 0, -42]);
  const centerScale = useTransform(scrollYProgress, [0, 0.24, 0.82, 1], [0.9, 1, 1, 0.94]);
  const metricsOpacity = useTransform(scrollYProgress, [0.42, 0.54, 0.8, 0.94], [0, 1, 1, 0]);
  const metricsY = useTransform(scrollYProgress, [0.42, 0.54, 0.8, 0.94], [34, 0, 0, -24]);

  return (
    <section
      ref={ref}
      id="meet"
      aria-label={t(lang, "meet.aria")}
      className="relative scroll-mt-12 overflow-hidden bg-[#f8f9fa] px-5 pb-16 pt-12 sm:px-10 lg:pb-[5.5rem] lg:pt-14"
    >
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-[linear-gradient(180deg,rgba(248,249,250,0)_0%,rgba(248,249,250,.82)_55%,rgba(248,249,250,1)_100%)]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[860px] opacity-70 lg:h-[1010px]"
        style={{
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0%, black 12%, black 88%, transparent 100%)",
          maskImage:
            "linear-gradient(to bottom, transparent 0%, black 12%, black 88%, transparent 100%)",
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "radial-gradient(circle, rgba(82,35,177,0.11) 1px, transparent 1px)",
            backgroundSize: "18px 18px",
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.35) 8%, black 18%, black 82%, rgba(0,0,0,0.35) 92%, transparent 100%)",
            maskImage:
              "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.35) 8%, black 18%, black 82%, rgba(0,0,0,0.35) 92%, transparent 100%)",
          }}
        />
      </div>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[180px] bg-[#f8f9fa] lg:h-[160px]"
      />

      <div className="relative mx-auto w-full max-w-[1200px]">
        <motion.div className="mx-auto max-w-[930px] text-center" style={{ opacity: headerOpacity, y: headerY }}>
          <div className="inline-flex w-fit rounded-full bg-[#f3eefa] px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#5223b1]">
            {t(lang, "meet.badge")}
          </div>
          <h2 className="mt-5 text-[34px] font-extrabold leading-[1.08] tracking-[-0.03em] text-[#100f12] sm:text-[48px] md:text-[58px]">
            {t(lang, "meet.title")}
          </h2>
          <p className="mx-auto mt-6 max-w-[760px] text-base leading-relaxed text-[#3a383f] sm:text-lg">
            {t(lang, "meet.subtitle")}
          </p>
        </motion.div>

        <div className="relative mt-4 hidden h-[560px] w-full overflow-visible lg:block">
          <svg
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-1/2 top-0 h-full w-[1180px] -translate-x-1/2 overflow-visible"
            viewBox="0 0 1180 560"
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <linearGradient id="meetLinkGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#5223b1" />
                <stop offset="58%" stopColor="#8c62de" />
                <stop offset="100%" stopColor="#d923c7" />
              </linearGradient>
            </defs>
            {capabilities.map((item) => (
              <MeetLine key={item.key} x={item.x} progress={scrollYProgress} />
            ))}
          </svg>

          <motion.div
            className="absolute left-1/2 top-[36px] -ml-[110px] flex w-[220px] flex-col items-center text-center"
            style={{ opacity: centerOpacity, y: centerY, scale: centerScale }}
          >
            <img src={logoMarkSrc} alt="" className="h-20 w-20 object-contain" />
            <div className="mt-3 text-[24px] font-extrabold leading-none text-[#17151c]">ClawEase</div>
            <div className="mt-2 text-[13px] font-bold text-[#7a6079]">{t(lang, "meet.center.role")}</div>
          </motion.div>

          {capabilities.map((item, index) => (
            <CapabilityCard key={item.key} lang={lang} item={item} progress={scrollYProgress} index={index} />
          ))}
        </div>

        <div className="relative z-10 mt-14 hidden lg:block">
          <MeetMetrics lang={lang} opacity={metricsOpacity} y={metricsY} />
        </div>

        <div className="mt-12 lg:hidden">
          <motion.div
            className="mx-auto mb-6 flex max-w-[320px] flex-col items-center text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
          >
            <img src={logoMarkSrc} alt="" className="h-16 w-16 object-contain" />
            <div className="text-[21px] font-extrabold leading-none text-[#17151c]">ClawEase</div>
            <div className="mt-2 text-[13px] font-bold text-[#7a6079]">{t(lang, "meet.center.role")}</div>
          </motion.div>
          <div className="grid grid-cols-2 gap-4">
            {capabilities.map((item) => (
              <MobileCapabilityCard key={item.key} lang={lang} item={item} />
            ))}
          </div>
          <div className="relative z-10 mt-14">
            <MeetMetrics lang={lang} />
          </div>
        </div>
      </div>
    </section>
  );
}
